import { Add, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import ClubTabLayout from './ClubTabLayout';
import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useConfirm } from 'material-ui-confirm';
import { useFetch } from '../../../../hooks/useFecth';
import { useSearchTextInput } from '../../../../hooks/useSearchTextInput';
import server from '../../../../services/server';
import useErrors from '../../../../hooks/useErrors';

const roles = [
    { label: 'Administrador', value: 'admin' },
    { label: 'Gerente', value: 'manager' },
    { label: 'Usuário', value: 'user' },
    { label: 'Responsável', value: 'responsible' },
    { label: 'Dono do clube', value: 'owner' },
];

const memberSchema = yup.object().shape({
    userName: yup.string().email('Informe um e-mail válido').required('Informe o e-mail'),
    password: yup.string().when('isEditing', {
        is: false,
        then: schema => schema.required('Informe a senha'),
        otherwise: schema => schema,
    }),
    role: yup.string().required(),
});

const MemberDialog = ({ clubId, open, onClose, assignment, onSuccess }) => {
    const { setErrors, enqueueSnackbar } = useErrors();
    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: memberSchema,
        initialValues: {
            userName: assignment?.user?.userName || '',
            password: '',
            role: assignment?.role || 'user',
            status: typeof assignment?.status === 'boolean' ? assignment.status : true,
            isEditing: Boolean(assignment),
        },
        onSubmit: async values => {
            try {
                if (assignment?.id) {
                    await server.put(`clubs/${clubId}/members/${assignment.id}`, {
                        role: values.role,
                        status: values.status,
                        password: values.password || undefined,
                    });
                    enqueueSnackbar('Membro atualizado com sucesso.', { variant: 'success' });
                } else {
                    await server.post(`clubs/${clubId}/members`, {
                        role: values.role,
                        status: values.status,
                        password: values.password || undefined,
                        userName: values.userName.trim(),
                        clubId: clubId || undefined,
                    });
                    enqueueSnackbar('Membro criado com sucesso.', { variant: 'success' });
                }

                await onSuccess();
                onClose();
            } catch (error) {
                setErrors(error);
            }
        },
    });



    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{assignment ? 'Editar membro' : 'Novo membro'}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} pt={1}>
                    <TextField
                        label="E-mail"
                        name="userName"
                        type="email"
                        value={formik.values.userName}
                        onChange={formik.handleChange}
                        fullWidth
                        disabled={Boolean(assignment)}
                        error={!assignment && formik.touched.userName && Boolean(formik.errors.userName)}
                        helperText={
                            assignment
                                ? 'O e-mail não pode ser alterado. Para outro usuário, remova o membro e adicione de novo.'
                                : (formik.touched.userName && formik.errors.userName) ||
                                  'Se o e-mail já existir, ele será vinculado a este clube.'
                        }
                    />
                    <TextField
                        label="Senha"
                        name="password"
                        type="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        fullWidth
                        helperText={assignment ? 'Preencha apenas se deseja redefinir a senha.' : 'Obrigatório para novo usuário.'}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                    />
                    <TextField
                        select
                        label="Cargo"
                        name="role"
                        value={formik.values.role}
                        onChange={formik.handleChange}
                        fullWidth
                    >
                        {roles.map(role => (
                            <MenuItem key={role.value} value={role.value}>
                                {role.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <FormControlLabel
                        control={<Switch name="status" checked={formik.values.status} onChange={formik.handleChange} />}
                        label="Ativo"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={formik.handleSubmit} variant="contained" disabled={formik.isSubmitting}>
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default function ListClubMembersAdm({ clubId }) {
    const { search, input, handleChange, handleReset } = useSearchTextInput();
    const { setErrors, enqueueSnackbar } = useErrors();
    const confirm = useConfirm();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [page, setPage] = useState(0);
    const perPage = 10;

    const params = useMemo(
        () => ({
            params: {
                page,
                perPage,
                search: search || undefined,
                clubId: clubId || undefined,
            },
        }),
        [page, perPage, search, clubId],
    );

    const { data: listResponse, mutate, isFetching } = useFetch(`clubs/${clubId}/members`, params);

    const members = listResponse?.data ?? [];
    const total = listResponse?.total ?? 0;
    const pages = listResponse?.pages ?? 1;
    const ownerCount = listResponse?.ownerCount ?? members.filter(member => member.role === 'owner').length;

    useEffect(() => {
        setPage(0);
    }, [search]);

    const closeDialog = () => {
        setDialogOpen(false);
        setEditing(null);
    };

    const handleDelete = async member => {
        if (!member?.id) return;
        try {
            await confirm({
                title: 'Remover membro',
                description:
                    'Tem certeza que deseja remover este usuário do clube? Ele perderá o acesso à administração do clube.',
                confirmationText: 'Remover',
                confirmationButtonProps: { color: 'error', variant: 'contained' },
            });
            await server.delete(`clubs/${clubId}/members/${member.id}`);
            enqueueSnackbar('Membro removido com sucesso.', { variant: 'success' });
            await mutate();
        } catch (error) {
            if (error?.response) setErrors(error);
        }
    };

    return (
        <>
            <ClubTabLayout
                actions={
                    <>
                        <TextField
                            size="small"
                            label="Buscar por e-mail"
                            value={input}
                            onChange={event => handleChange(event.target.value)}
                            sx={{ mr: 'auto', minWidth: { xs: '100%', sm: 260 } }}
                            InputProps={{
                                endAdornment: input ? (
                                    <Button size="small" onClick={handleReset}>
                                        Limpar
                                    </Button>
                                ) : null,
                            }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => {
                                setEditing(null);
                                setDialogOpen(true);
                            }}
                        >
                            Novo usuário
                        </Button>
                    </>
                }
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>E-mail</TableCell>
                            <TableCell>Cargo</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Criado em</TableCell>
                            <TableCell align="right" width={100}>
                                Ações
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {members?.map(item => (
                            <TableRow key={item.id} hover>
                                <TableCell>{item.user?.userName}</TableCell>
                                <TableCell>{roles.find(r => r.value === item.role)?.label ?? item.role}</TableCell>
                                <TableCell>{item.status ? 'Ativo' : 'Inativo'}</TableCell>
                                <TableCell align="right">
                                    {item.user?.createdAt ? new Date(item.user.createdAt).toLocaleDateString('pt-BR') : '--'}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setEditing(item);
                                            setDialogOpen(true);
                                        }}
                                        aria-label="Editar"
                                    >
                                        <Edit fontSize="small" />
                                    </IconButton>
                                    {item.role !== 'owner' || ownerCount > 1 ? (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(item)}
                                            aria-label="Excluir"
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {members?.length === 0 && !isFetching && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 3 }} textAlign="center">
                        Nenhum membro cadastrado.
                    </Typography>
                )}
                {pages > 1 && (
                    <Box display="flex" justifyContent="center" alignItems="center" gap={1} sx={{ px: 2, py: 1.5 }}>
                        <Button variant="outlined" size="small" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
                            Anterior
                        </Button>
                        <Typography variant="body2">
                            Página {page + 1} de {pages} ({total} membros)
                        </Typography>
                        <Button variant="outlined" size="small" disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>
                            Próxima
                        </Button>
                    </Box>
                )}
            </ClubTabLayout>

            <MemberDialog clubId={clubId} open={dialogOpen} onClose={closeDialog} assignment={editing} onSuccess={() => mutate()} />
        </>
    );
}
